import ReactGA from 'react-ga4'
import { setPostHogPersonMetadata, capturePostHogEvent } from 'client/lib/posthog'
import * as crisp from 'client/lib/crisp'
import * as rollbar from 'client/lib/rollbar'

const IS_DEV = process.env.NODE_ENV === 'development'
const SERVER_SIDE = typeof window === 'undefined'
const DISABLED = (SERVER_SIDE || !CONFIG.googleAnalytics.enabled)

export const setup = () => {
  if (DISABLED) return

  ReactGA.initialize([
    {
      trackingId: CONFIG.googleAnalytics.id
    },
    {
      trackingId: CONFIG.googleAnalytics.ga4id
    }
  ])
}

export const pageView = () => {
  if (DISABLED) return

  ReactGA.pageview(window.location.pathname + window.location.search)
}

export const modalView = (modal) => {
  if (DISABLED) return

  ReactGA.pageview(modal)
}

// These indicate a user has an account
// sometimes they will have an account from the past
// and are just signing in which is ok.
export const SIGNUP_ACTIONS = {
  CREATED_ACCOUNT: 'Created an Account',

  LOGGED_IN_ORCID: 'Logged in - Orcid',
  SIGNED_UP_ORCID: 'Signed up - Orcid',

  LOGGED_IN_FACEBOOK: 'Logged in - Facebook',
  SIGNED_UP_FACEBOOK: 'Signed up - Facebook',

  LOGGED_IN_GOOGLE: 'Logged in - Google',
  SIGNED_UP_GOOGLE: 'Signed up - Google'
}

export const SIGNUP_METHODS = {
  'Created an Account': 'local',

  'Logged in - Orcid': 'orcid',
  'Signed up - Orcid': 'orcid',

  'Logged in - Facebook': 'facebook',
  'Signed up - Facebook': 'facebook',

  'Logged in - Google': 'google',
  'Signed up - Google': 'google'
}

// These are predefined tiktok events found: https://ads.tiktok.com/help/article?aid=10028
const TIKTOK_EVENTS = {
  REGISTRATION: 'Registration',
  CHECKOUT: 'Checkout',
  START_CHECKOUT: 'StartCheckout'
}

// pixelTrack will send events to a given ad pixel to build audiences
// in the respective ad platforms.
const pixelTrack = (event) => {
  let gtagType, tiktokType
  if (Object.values(SIGNUP_ACTIONS).includes(event.action)) {
    gtagType = CONFIG.gtag.signup
    tiktokType = TIKTOK_EVENTS.REGISTRATION
  } else if (event.action === 'Purchased Plan' && event.label === 'basic') {
    gtagType = CONFIG.gtag.subscribeToBasic
    tiktokType = TIKTOK_EVENTS.CHECKOUT
  } else if (event.action === 'Purchased Plan' && event.label === 'premium') {
    gtagType = CONFIG.gtag.subscribeToPremium
    tiktokType = TIKTOK_EVENTS.CHECKOUT
  } else if (event.action === 'Purchased Plan') {
    gtagType = CONFIG.gtag.subscribeToPremiumPlus
    tiktokType = TIKTOK_EVENTS.CHECKOUT
  } else if (event.action === 'View Checkout') {
    gtagType = CONFIG.gtag.viewCheckout
    tiktokType = TIKTOK_EVENTS.START_CHECKOUT
  } else if (event.action === 'View Pricing Page') {
    gtagType = CONFIG.gtag.viewPricingPage
    tiktokType = TIKTOK_EVENTS.START_CHECKOUT
  } else {
    return
  }
  if (typeof gtag !== 'undefined') { // eslint-disable-line
    gtag('event', 'conversion', { // eslint-disable-line
      send_to: gtagType
    })
  }
  if (typeof ttq !== 'undefined') { // eslint-disable-line
    ttq.track(tiktokType) // eslint-disable-line
  }
}

export const event = (event, { pushToCrisp = true } = {}) => {
  const { action, ...rest } = event

  if (IS_DEV) {
    console.log('GA event:', event)
  }

  if (DISABLED) return

  ReactGA.event(event)
  pixelTrack(event)

  // add sign up conversion event
  // with oauth we treat logins as sign ups
  // analytics can dedupe these into actual sign ups
  if (Object.values(SIGNUP_ACTIONS).includes(action)) {
    gtag('event', 'sign_up', {
      method: SIGNUP_METHODS[action]
    })
  }

  if (window.$crisp && pushToCrisp && action) {
    const crispData = ['set', 'session:event', [[[action, rest]]]]
    window.$crisp.push(crispData)
  }

  capturePostHogEvent({
    name: `${event.category}: ${event.action}`,
    metadata: event
  })
}

// This is for events that are custom GA hooks like purchasing
export const gtag = (...args) => {
  if (DISABLED) return

  ReactGA.gtag(...args)
}

const setUserGA = (user) => {
  if (DISABLED) return

  ReactGA.gtag('set', 'user_properties', {
    plan: user.subscription?.plan || 'free',
    subscriptionStatus: user.subscription?.status || 'inactive',
    orcid: user.orcid,
    signupType: user.signupType
  })
}

// setUser is a universal analytics function
// that captures setting a user's analytics in all analytics apps
// this is the only function that should be used to set up a user in analytics
export const setUser = (user) => {
  setPostHogPersonMetadata(user)
  crisp.setUser(user)
  rollbar.setUser(user)
  setUserGA(user)
  if (typeof profitwell !== 'undefined') {
    profitwell('start', {
      user_email: user.email
    })
  }
}
